if (!customElements.get('cart-urgency-tooltip')) {
  class CartUrgencyTooltip extends HTMLElement {
    cartAnchorSelector = '.js-drawer-open-cart';
    showDelay = 2000;
    cooldown = 12 * 60 * 60 * 1000; // 12 hours in production: 12 * 60 * 60 * 1000
    storageKeys = {
      visited: 'cartUrgencyVisited',
      lastAddToCart: 'cartUrgencyLastAddToCart',
      addedThisSession: 'cartUrgencyAddedThisSession',
      dismissed: 'cartUrgencyDismissed',
      shownThisSession: 'cartUrgencyShownThisSession',
    };

    constructor() {
      super();
    }

    connectedCallback() {
      this.anchor = document.querySelector(this.cartAnchorSelector);
      if (!this.anchor) return;

      // Move tooltip inside the cart anchor so it's naturally positioned
      // relative to it — this means no JS scroll/resize tracking is needed.
      this.anchor.style.position = 'relative';
      if (this.parentElement !== this.anchor) this.anchor.appendChild(this);

      this.closeButton = this.querySelector('[data-cart-urgency-close]');
      this.closeButton?.addEventListener('click', this.onDismiss);

      // When user clicks the cart icon itself, hide the tooltip
      this.anchor.addEventListener('click', this.onAnchorClick);

      document.addEventListener('ajaxProduct:added', this.onAddToCart);
      document.addEventListener('cart:updated', this.onCartUpdated);

      this.markVisitedAndMaybeShow();
    }

    disconnectedCallback() {
      clearTimeout(this.showTimeout);
      this.closeButton?.removeEventListener('click', this.onDismiss);
      this.anchor?.removeEventListener('click', this.onAnchorClick);
      document.removeEventListener('ajaxProduct:added', this.onAddToCart);
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }

    get itemCount() {
      return parseInt(this.dataset.itemCount, 10) || 0;
    }

    readStorage(storage, key) {
      try {
        return storage.getItem(key);
      } catch (_) {
        return null;
      }
    }

    writeStorage(storage, key, value) {
      try {
        storage.setItem(key, value);
      } catch (_) {
        // Storage unavailable (private mode, blocked) — fail silently
      }
    }

    markVisitedAndMaybeShow() {
      const hasVisitedBefore = this.readStorage(localStorage, this.storageKeys.visited) === 'true';

      if (!hasVisitedBefore) {
        this.writeStorage(localStorage, this.storageKeys.visited, 'true');
        return;
      }

      if (!this.shouldShow()) return;
      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: 'cart-urgency:eligible' });
      this.showTimeout = setTimeout(() => this.show(), this.showDelay);
    }

    shouldShow() {
      if (this.itemCount <= 0) return false;
      if (this.readStorage(sessionStorage, this.storageKeys.shownThisSession) === 'true') return false;
      if (this.readStorage(sessionStorage, this.storageKeys.addedThisSession) === 'true') return false;
      if (this.readStorage(localStorage, this.storageKeys.dismissed) === 'true') return false;

      // Requires a recorded add-to-cart timestamp — if there is none, we can't
      // confirm the 12-hour window has passed, so treat it as not eligible.
      const lastAddToCart = parseInt(this.readStorage(localStorage, this.storageKeys.lastAddToCart), 10);
      if (!lastAddToCart) return false;

      return Date.now() - lastAddToCart >= this.cooldown;
    }

    show = () => {
      this.dataset.visible = 'true';
      this.writeStorage(sessionStorage, this.storageKeys.shownThisSession, 'true');
    };

    hide = () => {
      this.dataset.visible = 'false';
    };

    onAnchorClick = () => {
      this.hide();
    };

    onDismiss = (e) => {
      // Stop propagation so the close button click doesn't also open the cart
      e.stopPropagation();
      this.writeStorage(localStorage, this.storageKeys.dismissed, 'true');
      this.hide();
    };

    onAddToCart = () => {
      this.writeStorage(localStorage, this.storageKeys.lastAddToCart, String(Date.now()));
      this.writeStorage(sessionStorage, this.storageKeys.addedThisSession, 'true');
      clearTimeout(this.showTimeout);
      this.hide();
    };

    onCartUpdated = (event) => {
      if (event.detail?.cart?.item_count > 0) return;

      // Cart emptied — treat as purchase/reset so popup can show on a future visit
      try { localStorage.removeItem(this.storageKeys.dismissed); } catch (_) {}
      this.hide();
    };
  }

  customElements.define('cart-urgency-tooltip', CartUrgencyTooltip);
}
